/**
 * confidenceBand.ts — Phase-3 confidence penalties (`D-P3AU`)
 *
 * The one canonical place where a numeric state-transition confidence becomes a
 * categorical band, and where governed P3-D4 provider evidence may lower it.
 *
 * ── Two layers, deliberately distinct ─────────────────────────────────────
 *   numeric confidence  — how strong is the structural state-transition
 *                         evidence? Produced by the state-transition engine and
 *                         NEVER altered here.
 *   effective band      — how much confidence should be placed in that result
 *                         once governed provider-evidence reliability is
 *                         considered?
 *
 * So `confidence: 80` with `effectiveConfidenceBand: 'medium'` is correct when
 * provider confidence is MEDIUM. The band is the governed operational result,
 * not a rendering of the number.
 *
 * ── The ceiling ───────────────────────────────────────────────────────────
 * HIGH leaves the band alone · MEDIUM caps it at medium · LOW caps it at low ·
 * UNKNOWN and absence leave it alone. `IT CAN ONLY LOWER, NEVER RAISE` — an
 * already-low band is never promoted by good provider evidence, because the
 * structural evidence is what earned the low band in the first place.
 *
 * ── ONE EVENT, ONE INFLUENCE ──────────────────────────────────────────────
 * Only the aggregate `providerConfidence.state` is read. Its components are
 * deliberately ignored: P3-D4 already aggregates provider path, freshness and
 * disagreement under its own no-double-count invariant, so reading them again
 * here would count the same evidence twice.
 *
 * The exception this module exercises is narrow and belongs specifically to
 * providerConfidence → this band. It is NOT a general licence to map every
 * HIGH/MEDIUM/LOW concept onto every other.
 */

import type { ProviderConfidence } from './types';

export type ConfidenceBandLevel = 'high' | 'medium' | 'low';

/**
 * The banked thresholds, reused rather than re-chosen. They are the same values
 * the existing analysis band derivation uses, so both surfaces agree.
 */
export const CONFIDENCE_BAND_HIGH_MIN   = 75;
export const CONFIDENCE_BAND_MEDIUM_MIN = 40;

/** Ordered weakest-first, so a ceiling is a simple index lookup. */
const ORDER: readonly ConfidenceBandLevel[] = ['low', 'medium', 'high'];

/**
 * The base band: derived from the numeric confidence alone, with no provider
 * input. `null` when there is no numeric confidence to classify — absence is
 * not a band, and must not become `'low'`.
 */
export function baseConfidenceBand(confidence: number): ConfidenceBandLevel | null {
  if (typeof confidence !== 'number' || !Number.isFinite(confidence)) return null;
  if (confidence >= CONFIDENCE_BAND_HIGH_MIN)   return 'high';
  if (confidence >= CONFIDENCE_BAND_MEDIUM_MIN) return 'medium';
  return 'low';
}

/**
 * The ceiling a given provider-confidence state imposes, or `null` for "no
 * ceiling at all". `UNKNOWN`, absence and any unrecognised value all mean no
 * ceiling: `ABSENCE OF RELIABLE EVIDENCE MUST NOT SILENTLY BECOME A PENALTY`.
 */
function ceilingFor(providerConfidence: ProviderConfidence | null | undefined): ConfidenceBandLevel | null {
  const state = providerConfidence?.state;
  if (state === 'MEDIUM') return 'medium';
  if (state === 'LOW')    return 'low';
  return null;              // HIGH, UNKNOWN, absent, unrecognised
}

/**
 * The governed effective band: the base band, lowered to the provider ceiling
 * when one applies.
 */
export function effectiveConfidenceBandFor(
  confidence: number,
  providerConfidence: ProviderConfidence | null | undefined,
): ConfidenceBandLevel | null {
  const base = baseConfidenceBand(confidence);
  if (base === null) return null;

  const ceiling = ceilingFor(providerConfidence);
  if (ceiling === null) return base;

  // Lower only. `Math.min` over the weakest-first order expresses the ceiling
  // without ever promoting a band.
  return ORDER[Math.min(ORDER.indexOf(base), ORDER.indexOf(ceiling))];
}

/**
 * Attach the governed band to a state-transition result.
 *
 * Exists as its own function so the composition seam is directly testable: the
 * real engine's confidence depends on market structure, so a synthetic fixture
 * cannot reliably produce every band, and a test that never reaches `medium`
 * would silently stop proving that the ceiling is applied exactly ONCE.
 *
 * Additive by construction — it spreads the engine result and adds one field,
 * so no existing value can be altered here.
 */
export function withEffectiveConfidenceBand<T extends { confidence: number }>(
  stateTransition: T,
  providerConfidence: ProviderConfidence | null | undefined,
): T & { effectiveConfidenceBand: ConfidenceBandLevel | null } {
  return {
    ...stateTransition,
    effectiveConfidenceBand: effectiveConfidenceBandFor(
      stateTransition.confidence, providerConfidence),
  };
}
